// src/admin/pages/PlayerLifetimeRTP.tsx — Player Lifetime RTP Monitor
import { useState, useEffect, useCallback } from 'react';
import useAdmin from '../hooks/useAdmin';
import { Loader2, Shield, Trophy, AlertTriangle, Search } from 'lucide-react';

interface LifetimeRecord {
  user_id: string;
  email: string;
  name: string;
  total_bet: number;
  total_won: number;
  lifetime_rtp: number;
  vip_level: number;
  biggest_win: number;
  jackpots_won: number;
  session_count: number;
  risk_flag: boolean;
  last_session_at: string;
}

const VIP_BADGES = ['Regular', 'Silver', 'Gold', 'Platinum'];
const VIP_COLORS = ['text-gray-600', 'text-gray-400', 'text-yellow-600', 'text-cyan-600'];
const VIP_BG    = ['bg-gray-50', 'bg-gray-100', 'bg-yellow-50', 'bg-cyan-50'];

export default function PlayerLifetimeRTPPage() {
  const { token, error, setError } = useAdmin();
  const [tab, setTab] = useState<'leaderboard' | 'risk'>('leaderboard');
  const [leaderboard, setLeaderboard] = useState<LifetimeRecord[]>([]);
  const [riskPlayers, setRiskPlayers]  = useState<LifetimeRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [lbRes, riskRes] = await Promise.all([
        fetch('/admin/lifetime-rtp/leaderboard', { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' }),
        fetch('/admin/lifetime-rtp/risk-players', { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' }),
      ]);
      setLeaderboard(await lbRes.json());
      setRiskPlayers(await riskRes.json());
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  }, [token, setError]);

  useEffect(() => { load(); }, [load]);

  const fmt = (n: number) => n?.toLocaleString('en-IN', { maximumFractionDigits: 0 }) ?? '0';
  const filtered = (tab === 'leaderboard' ? leaderboard : riskPlayers).filter(p =>
    !search || p.email?.toLowerCase().includes(search.toLowerCase()) || p.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900">Player Lifetime RTP</h2>
        <p className="text-gray-500 mt-1">Cross-session player tracking — VIP levels, risk flags, lifetime statistics</p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded flex justify-between">
          <span>{error}</span><button onClick={() => setError('')} className="font-bold">✕</button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow p-4 border border-gray-200 text-center">
          <Trophy size={24} className="mx-auto mb-2 text-yellow-500" />
          <div className="text-2xl font-bold text-gray-900">{leaderboard.length}</div>
          <div className="text-sm text-gray-500">Tracked Players</div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border border-gray-200 text-center">
          <AlertTriangle size={24} className="mx-auto mb-2 text-red-500" />
          <div className="text-2xl font-bold text-red-700">{riskPlayers.length}</div>
          <div className="text-sm text-gray-500">Risk Flagged</div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border border-gray-200 text-center">
          <Shield size={24} className="mx-auto mb-2 text-cyan-500" />
          <div className="text-2xl font-bold text-gray-900">
            {leaderboard.filter(p => p.vip_level >= 3).length}
          </div>
          <div className="text-sm text-gray-500">Platinum VIPs</div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border border-gray-200 text-center">
          <div className="text-2xl font-bold text-indigo-700">
            {leaderboard.length > 0
              ? (leaderboard.reduce((s, p) => s + Number(p.lifetime_rtp), 0) / leaderboard.length).toFixed(1) + '%'
              : '—'}
          </div>
          <div className="text-sm text-gray-500">Avg Lifetime RTP</div>
        </div>
      </div>

      {/* Tabs + Search */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="flex gap-2">
          {(['leaderboard', 'risk'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-lg font-medium text-sm ${
                tab === t ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}>
              {t === 'leaderboard' ? '🏆 Leaderboard' : '⚠️ Risk Players'}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
          <input type="text" placeholder="Search by name or email..." value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 w-64" />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-indigo-600" size={40} /></div>
      ) : (
        <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm">
                <tr>
                  <th className="p-3 text-left">#</th>
                  <th className="p-3 text-left">Player</th>
                  <th className="p-3 text-center">VIP</th>
                  <th className="p-3 text-right">Total Bet</th>
                  <th className="p-3 text-right">Total Won</th>
                  <th className="p-3 text-center">Lifetime RTP</th>
                  <th className="p-3 text-center">Biggest Win</th>
                  <th className="p-3 text-center">Jackpots</th>
                  <th className="p-3 text-center">Sessions</th>
                  <th className="p-3 text-center">Risk</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, i) => (
                  <tr key={p.user_id} className={`border-b hover:bg-gray-50 ${p.risk_flag ? 'bg-red-50' : VIP_BG[p.vip_level] || ''}`}>
                    <td className="p-3 text-gray-400 text-sm">{i + 1}</td>
                    <td className="p-3">
                      <div className="font-medium text-gray-900">{p.name}</div>
                      <div className="text-xs text-gray-500">{p.email}</div>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`font-semibold text-sm ${VIP_COLORS[p.vip_level]}`}>
                        {VIP_BADGES[p.vip_level] || 'Regular'}
                      </span>
                    </td>
                    <td className="p-3 text-right font-mono text-sm">${fmt(p.total_bet)}</td>
                    <td className="p-3 text-right font-mono text-sm font-semibold text-green-700">${fmt(p.total_won)}</td>
                    <td className="p-3 text-center">
                      <span className={`font-bold text-lg ${
                        Number(p.lifetime_rtp) > 102 ? 'text-red-600' :
                        Number(p.lifetime_rtp) > 97  ? 'text-yellow-600' :
                        'text-green-700'
                      }`}>
                        {Number(p.lifetime_rtp).toFixed(1)}%
                      </span>
                    </td>
                    <td className="p-3 text-center font-mono text-sm">${fmt(p.biggest_win)}</td>
                    <td className="p-3 text-center font-semibold">{p.jackpots_won}</td>
                    <td className="p-3 text-center text-gray-500 text-sm">{p.session_count}</td>
                    <td className="p-3 text-center">
                      {p.risk_flag
                        ? <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold flex items-center gap-1 justify-center">
                            <AlertTriangle size={12} /> HIGH
                          </span>
                        : <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">OK</span>}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={10} className="p-8 text-center text-gray-400">No records found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
